import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { styles } from './UploadFromComputer.style';
import { Collapse, IconButton, Stack, TextField, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { LocalizationContext } from 'src/Context/LocalizationContext';
import ComputerIcon from 'src/icons/fileUpload/ComputerIcon';
import { useQuerySendQuestionaryByEmail } from 'src/hooks/useQuerySendQuestionaryByEmail';
import { useAnalytics } from 'src/hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from 'src/types/RefineAnalyticsEvents';
import { ArrowUpIcon } from '@icons/ArrowUpIcon';
import { ArrowDownIcon } from '@icons/ArrowDownIcon';

interface IUploadFromComputerProps {
	setEmailData: Function;
}

export default function UploadFromComputerComponent(props: IUploadFromComputerProps) {
	const [isCollapsed, setIsCollapsed] = useState(false);
	const [iconColor, setIconColor] = useState<string>(styles.collapsedColor);
	const [email, setEmail] = useState<string>(''); // Email input
	const [isEmailValid, setIsEmailValid] = useState<boolean>(false); // Email validation
	const { trackRefineEvent } = useAnalytics();

	const { sendQuestionaryByEmail, isLoading: isSendingEmail } = useQuerySendQuestionaryByEmail();

	const { FileUpload: text } = useContext(LocalizationContext);

	const onArrowClick = () => {
		setIsCollapsed(!isCollapsed);
	};

	const onEmailSendClick = async () => {
		if (!isEmailValid) return;
		await sendQuestionaryByEmail(email);
		props.setEmailData({ email, emailSent: true });
		trackRefineEvent(REFINE_ANALYTICS_EVENTS.SEND_QUESTIONNAIRE_LINK_TO_EMAIL);
	};

	// Set icon color based on isCollapsed state
	useEffect(() => {
		setIconColor(!isCollapsed ? styles.collapsedColor : styles.expandedColor);
	}, [isCollapsed]);

	// Validate email
	useEffect(() => {
		if (email.length > 0) {
			const isValid = email
				.trim()
				.match(
					/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
				);
			setIsEmailValid(isValid !== null);
		}
		// Remove red error from TextInput border when input is empty
		else {
			setIsEmailValid(true);
		}
	}, [email]);

	return (
		<div>
			{/* Collapsed Container */}
			<Stack
				direction="row"
				alignItems={'center'}
				sx={[styles.container, isCollapsed ? styles.expandedContainer : styles.collapsedContainer]}
				justifyContent={'space-between'}
				onClick={onArrowClick}>
				<Stack gap="20px" alignItems={'center'} direction={'row'}>
					<ComputerIcon primaryColor={iconColor} />
					<Stack direction="column">
						{/* Header */}
						<Typography variant={'supportingDocumentsTitle'} color={'textPrimary'}>
							{text.UploadFromComputerButtonHeader}
						</Typography>
						{/* Subheader */}
						<Typography variant={'supportingDocumentsSubtitle'} color={'textSecondary'}>
							{text.UploadFromComputerButtonSubheader}
						</Typography>
					</Stack>
				</Stack>
				{/* Expand Button */}
				<IconButton onClick={onArrowClick} data-cy="pqSendQuestionnaireToEmailExpandButton">
					{isCollapsed ? <ArrowUpIcon /> : <ArrowDownIcon />}
				</IconButton>
			</Stack>
			{/* Fade effect */}
			<Collapse in={isCollapsed} timeout={150}>
				{/* Expanded Container */}
				<Stack
					paddingInline={'16px'}
					direction="column"
					gap={'20px'}
					sx={[styles.container, styles.collapsibleContainer]}
					alignItems={'stretch'}>
					{/* Send email */}
					<Stack width={'100%'} maxWidth={'400px'} direction="row" textAlign={'start'} spacing={1}>
						{/* Email field */}
						<TextField
							fullWidth
							id="upload-file-from-computer-email"
							data-cy="pqSendQuestionnaireToEmailInputField"
							label={text.UploadFromComputerEmailInput}
							helperText={text.UploadFromComputerEmailHelpText}
							value={email}
							onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
							error={!isEmailValid}
						/>
						{/* Email send button */}
						<LoadingButton
							data-cy="pqSendQuestionnaireToEmailSubmitButton"
							variant="contained"
							disabled={email.length === 0 || !isEmailValid}
							loading={isSendingEmail}
							onClick={onEmailSendClick}
							sx={styles.emailSendButton}>
							{text.UploadFromComputerEmailSendButton}
						</LoadingButton>
					</Stack>
				</Stack>
			</Collapse>
		</div>
	);
}
